import React, { useState, useRef, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import styles from "../styles/AIAssistant.module.css";

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000';

const formatMessage = (text) => {
  let formatted = text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/^\* (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')
    .replace(/\n\n/g, '<br/><br/>')
    .replace(/\n/g, ' ');

  return <span dangerouslySetInnerHTML={{ __html: formatted }} />;
};

const AIAssistant = ({ userRunData }) => {
  const [messages, setMessages] = useState([
    {
      role: "ai",
      text: "Hi! I'm Chase, your AI running coach. I can help you plan future races, create training schedules, and track your progress toward visiting all 50 states. What would you like to work on today?",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const messagesEndRef = useRef(null);

  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const suggestions = [
    "Plan a 4-month marathon schedule in Southern California",
    "Create a training plan for my first 5K",
    "Suggest races in states I haven't visited yet",
    "What's a good race in Texas this spring?",
    "Help me plan to visit all 50 states",
  ];

  const handleSuggestionClick = (suggestion) => {
    sendMessage(null, suggestion);
  };

  const sendMessage = async (e, messageText = null) => {
    if (e) e.preventDefault();
    const textToSend = messageText || input;
    if (!textToSend.trim() || loading) return;

    const userMessage = { role: "user", text: textToSend };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);
    setShowSuggestions(false);

    try {
      const token = await getAccessTokenSilently();

      // Include conversation history (last 10 messages for context)
      const conversationHistory = messages.slice(-10).map((msg) => ({
        role: msg.role === "user" ? "user" : "assistant",
        content: msg.text,
      }));

      // Add the current message
      conversationHistory.push({
        role: "user",
        content: textToSend,
      });

      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };

      const response = await fetch(`${API_URL}/api/ai/chat`, {
        method: "POST",
        headers: headers,
        body: JSON.stringify({
          message: textToSend,
          userRunData: userRunData,
          conversationHistory: conversationHistory,
        }),
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error("10. Error response:", errorData);
        throw new Error(
          `Server responded with ${response.status}: ${errorData}`
        );
      }

      const data = await response.json();

      if (data.success) {
        setMessages((prev) => [...prev, { role: "ai", text: data.reply }]);
      }
    } catch (error) {
      console.error("=== ERROR ===");
      console.error("Error name:", error.name);
      console.error("Error message:", error.message);
      console.error("Full error:", error);

      const isQuotaExceeded = error.message?.includes('429') || error.message?.includes('RESOURCE_EXHAUSTED');
      const isOverloaded = error.message?.includes('UNAVAILABLE') || error.message?.includes('503');

      let friendlyMessage = "Something went wrong. Please try again.";
      if (isQuotaExceeded) {
        friendlyMessage = "I've hit my daily limit for responses. Please try again tomorrow, or contact support if this keeps happening.";
      } else if (isOverloaded) {
        friendlyMessage = "I'm a bit overloaded right now! Please try asking again in a moment.";
      }
      setMessages((prev) => [
        ...prev,
        {
          role: "ai",
          text: friendlyMessage,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>🏃‍♂️ AI Running Coach</h2>
        <p className={styles.subtitle}>
          Plan races • Get advice • Track progress
        </p>
      </div>

      <div className={styles.messagesContainer}>
        {messages.map((msg, index) => (
          <div key={index} className={`${styles.message} ${styles[msg.role]}`}>
            <div className={styles.messageContent}>
              <span className={styles.messageLabel}>{msg.role === "user" ? "You" : "🤖 Coach Chase"}:</span>
              <p>{formatMessage(msg.text)}</p>
            </div>
          </div>
        ))}

        {loading && (
          <div className={`${styles.message} ${styles.ai}`}>
            <div className={styles.messageContent}>
              <span className={styles.messageLabel}>🤖 Coach Chase:</span>
              <p className={styles.typing}>Planning your races...</p>
            </div>
          </div>
        )}

        {showSuggestions && messages.length === 1 && (
          <div className={styles.suggestionsContainer}>
            <p className={styles.suggestionsTitle}>Try asking:</p>
            <div className={styles.suggestions}>
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  className={styles.suggestionButton}
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={sendMessage} className={styles.inputContainer}>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask me to plan races, create schedules, or suggest routes..."
          className={styles.input}
          disabled={loading}
        />
        <button
          type="submit"
          className={styles.sendButton}
          disabled={loading || !input.trim()}
        >
          {loading ? "..." : "Send"}
        </button>
      </form>

      {userRunData && userRunData.totalRuns > 0 && (
        <div className={styles.statsFooter}>
          <div className={styles.stat}>
            <span className={styles.statValue}>{userRunData.totalRuns}</span>
            <span className={styles.statLabel}>Runs</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statValue}>{userRunData.totalStates}</span>
            <span className={styles.statLabel}>States</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statValue}>
              {50 - userRunData.totalStates}
            </span>
            <span className={styles.statLabel}>To Go</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAssistant;
